import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { Plus } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, getApiErrorMessage } from '@/shared/api/axios';
import type { MasterItem } from '@/features/master-data/hooks/useMasters';
import { Badge } from '@/shared/components/ui/Badge';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Modal } from '@/shared/components/ui/Modal';
import { Spinner } from '@/shared/components/ui/Spinner';

/** Add, rename and switch off the entries of one pick list (for example complaint types). */
export function MasterListModal({ masterType, title, onClose }: { masterType: string; title: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const key = ['masters-editor', masterType];
  const { data: items = [], isLoading } = useQuery({
    queryKey: key,
    queryFn: async () => (await api.get<{ items: MasterItem[] }>(`/api/masters/${masterType}?includeInactive=1`)).data.items,
  });
  const [name, setName] = useState('');
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: key });
    queryClient.invalidateQueries({ queryKey: ['masters', masterType] });
  };
  const run = async (fn: () => Promise<unknown>, done?: string) => {
    setBusy(true);
    try {
      await fn();
      refresh();
      if (done) toast.success(done);
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={title} footer={<div className="flex justify-end"><Button variant="ghost" onClick={onClose}>Close</Button></div>}>
      <div className="flex flex-col gap-3">
        <div className="flex gap-2">
          <Input name="master-new" placeholder="New entry" value={name} onChange={(e) => setName(e.target.value)} />
          <Button
            disabled={busy || !name.trim()}
            onClick={() => run(async () => { await api.post(`/api/masters/${masterType}`, { name: name.trim() }); setName(''); }, 'Added.')}
          >
            <Plus className="mr-1 h-4 w-4" aria-hidden /> Add
          </Button>
        </div>
        {isLoading ? (
          <div className="flex justify-center p-6"><Spinner /></div>
        ) : (
          <ul className="divide-y divide-outline rounded-md border border-outline">
            {items.map((item) => (
              <li key={item._id} className="flex items-center justify-between gap-2 px-3 py-2">
                {editing?.id === item._id ? (
                  <Input name="master-edit" value={editing.name} onChange={(e) => setEditing({ id: item._id, name: e.target.value })} />
                ) : (
                  <span className={item.isActive ? 'text-on-surface' : 'text-on-surface-variant line-through'}>{item.name}</span>
                )}
                <div className="flex shrink-0 items-center gap-1">
                  {!item.isActive && <Badge tone="neutral">Off</Badge>}
                  {editing?.id === item._id ? (
                    <Button size="sm" disabled={busy || !editing.name.trim()} onClick={() => run(async () => { await api.patch(`/api/masters/${masterType}/${item._id}`, { name: editing.name.trim() }); setEditing(null); }, 'Saved.')}>Save</Button>
                  ) : (
                    <Button size="sm" variant="ghost" onClick={() => setEditing({ id: item._id, name: item.name })}>Rename</Button>
                  )}
                  <Button size="sm" variant="ghost" disabled={busy} onClick={() => run(() => api.post(`/api/masters/${masterType}/${item._id}/${item.isActive ? 'deactivate' : 'activate'}`))}>
                    {item.isActive ? 'Switch off' : 'Switch on'}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
        <p className="text-xs text-on-surface-variant">A switched-off entry no longer appears in the pick list, but complaints that used it keep it.</p>
      </div>
    </Modal>
  );
}
